import { z } from 'zod';
import { issueMealsLineSchema, offlineTerminalPackageAuthorizationSchema, signedOfflineTerminalPackageSchema, } from './canteen.js';
const uuid = z.string().uuid();
const dateTime = z.string().datetime({ offset: true });
export const cardReaderProtocolVersionSchema = z.literal('1');
export const cardReaderPurposeSchema = z.enum([
    'cards.assign',
    'cards.replace',
    'canteen.identify',
    'canteen.issue',
]);
export const cardReaderChallengeSchema = z.object({
    id: uuid,
    nonce: z.string().min(32).max(200),
    expiresAt: dateTime,
});
export const cardReaderRequestSchema = z.discriminatedUnion('type', [
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('hello'),
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('status'),
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('set-sequence-floor'),
        /** A server-confirmed floor prevents a restarted agent from replaying. */
        sequenceFloor: z.number().int().nonnegative(),
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('read'),
        purpose: cardReaderPurposeSchema,
        institutionId: uuid,
        terminalId: uuid,
        challenge: cardReaderChallengeSchema.nullable(),
        timeoutMs: z.number().int().min(1_000).max(60_000).default(15_000),
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('cancel'),
        readRequestId: uuid,
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('install-offline-package'),
        /**
         * The Chrome extension verifies the Ed25519 signature before handing the
         * package to the local agent. A secure-origin PWA may verify it again as
         * defense in depth; the PWA must not retain it in IndexedDB.
         */
        package: signedOfflineTerminalPackageSchema,
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('offline-read'),
        institutionId: uuid,
        terminalId: uuid,
        timeoutMs: z.number().int().min(1_000).max(60_000).default(15_000),
    }),
]);
export const cardReaderStatusSchema = z.object({
    connected: z.boolean(),
    readerNames: z.array(z.string().min(1).max(300)),
    selectedReaderName: z.string().min(1).max(300).nullable(),
    cardPresent: z.boolean(),
    mode: z.enum(['pcsc', 'simulator']),
    diagnostic: z.enum(['ready', 'no_reader', 'pcsc_unavailable', 'simulated']),
});
export const installedOfflinePackageSchema = z.object({
    packageId: uuid,
    validUntil: dateTime,
});
export const cardReadAttestationPayloadSchema = z.object({
    version: cardReaderProtocolVersionSchema,
    requestId: uuid,
    purpose: cardReaderPurposeSchema,
    institutionId: uuid,
    terminalId: uuid,
    challenge: cardReaderChallengeSchema.nullable(),
    deviceId: uuid,
    readerName: z.string().min(1).max(300),
    sequence: z.number().int().positive(),
    readAt: dateTime,
    uid: z.string().regex(/^(?:[0-9A-F]{8}|[0-9A-F]{14}|[0-9A-F]{20})$/),
});
export const signedCardReadAttestationSchema = z.object({
    payload: cardReadAttestationPayloadSchema,
    // Ed25519 is used by the portable TypeScript agent.  The compact Windows
    // host uses the operating system's protected P-256 key store.
    algorithm: z.enum(['Ed25519', 'ECDSA-P256-SHA256']),
    keyId: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    signature: z.string().min(80).max(200),
});
/**
 * Privacy-preserving proof returned by a local terminal package. The browser
 * learns an entitled reservation projection, never a card UID or lookup key.
 */
export const offlineTerminalMatchPayloadSchema = z.object({
    version: cardReaderProtocolVersionSchema,
    requestId: uuid,
    institutionId: uuid,
    terminalId: uuid,
    deviceId: uuid,
    readerName: z.string().min(1).max(300),
    sequence: z.number().int().positive(),
    readAt: dateTime,
    /** Stable ID makes a retried reconciliation idempotent across services. */
    receiptId: uuid,
    packageId: uuid,
    consumerId: uuid,
    assignmentVersion: z.number().int().positive(),
    authorization: z.array(offlineTerminalPackageAuthorizationSchema),
});
export const signedOfflineTerminalMatchSchema = z.object({
    payload: offlineTerminalMatchPayloadSchema,
    algorithm: z.enum(['Ed25519', 'ECDSA-P256-SHA256']),
    keyId: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    signature: z.string().min(80).max(200),
});
/** Local append-only record queued by the PWA until the server is reachable. */
export const offlineTerminalReceiptSchema = z.object({
    receiptId: uuid,
    attestation: signedOfflineTerminalMatchSchema,
    serviceDate: z.string().date(),
    offerItemId: uuid,
    productionBatchId: uuid.nullable().default(null),
    reservationId: uuid,
    expectedReservationVersion: z.number().int().positive(),
    lines: z.array(issueMealsLineSchema).min(1),
    occurredAt: dateTime,
    recordedLocallyAt: dateTime,
});
export const offlineTerminalReceiptBatchSchema = z.object({
    institutionId: uuid,
    terminalId: uuid,
    receipts: z.array(offlineTerminalReceiptSchema).min(1).max(100),
});
export const readerDeviceStateSchema = z.enum(['active', 'revoked']);
export const readerOfflinePackageKeySchema = z.object({
    keyId: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    publicKey: z.string().min(40).max(1_000),
});
/** Private Cards → Canteen bridge; it must never be exposed to a browser. */
export const createOfflinePackageTokensRequestSchema = z.object({
    deviceId: uuid,
    terminalId: uuid,
    /** Random 32-byte package lookup key, base64url encoded in transit. */
    lookupKey: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});
export const offlinePackageCardTokenSchema = z.object({
    token: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    consumerId: uuid,
    assignmentVersion: z.number().int().positive(),
});
export const offlinePackageCardTokensSchema = z.object({
    recipientKeyId: z.string().min(16).max(200),
    recipientPublicKey: z.string().min(80).max(4096),
    tokens: z.array(offlinePackageCardTokenSchema),
    skippedWithoutUidVault: z.number().int().nonnegative(),
});
export const readerDeviceSchema = z.object({
    deviceId: uuid,
    terminalId: uuid,
    label: z.string().min(1).max(120),
    keyId: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    offlinePackageKey: readerOfflinePackageKeySchema.nullable(),
    state: readerDeviceStateSchema,
    capabilities: z.array(z.literal('nfc_mifare_classic_1k_uid')),
    lastSequence: z.number().int().nonnegative(),
    enrolledAt: dateTime,
    lastSeenAt: dateTime.nullable(),
});
export const enrollReaderDeviceRequestSchema = z.object({
    deviceId: uuid,
    terminalId: uuid,
    label: z.string().trim().min(1).max(120),
    keyId: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    publicKey: z.string().min(40).max(1_000),
    // Older agents can remain enrolled for online reads. A signed offline
    // package is issued only after this independent P-256 key is registered.
    offlinePackageKey: readerOfflinePackageKeySchema.optional(),
    capabilities: z.array(z.literal('nfc_mifare_classic_1k_uid')).min(1).max(10),
});
export const createCardReadChallengeRequestSchema = z.object({
    deviceId: uuid,
    terminalId: uuid,
    purpose: cardReaderPurposeSchema,
});
export const verifyCardReadRequestSchema = z.object({
    attestation: signedCardReadAttestationSchema,
});
export const verifyOfflineTerminalMatchRequestSchema = z.object({
    attestation: signedOfflineTerminalMatchSchema,
});
export const verifiedCardReadResultSchema = z.object({
    id: uuid,
    purpose: cardReaderPurposeSchema,
    deviceId: uuid,
    terminalId: uuid,
    displayIdentifier: z.string().min(1).max(32),
    assignedPersonId: uuid.nullable(),
    assignedPersonName: z.string().min(1).max(200).nullable(),
    cardState: z
        .enum([
        'registered',
        'active',
        'blocked',
        'lost',
        'returned',
        'revoked',
        'replaced',
    ])
        .nullable(),
    // The UID never leaves the Cards boundary.  This derived identifier is an
    // opaque, institution-scoped reference used only by a verified command.
    mediumDerivedId: z.string().min(16).max(200),
    assignmentVersion: z.number().int().nonnegative(),
    readAt: dateTime,
    expiresAt: dateTime,
});
export const cardReaderResponseSchema = z.discriminatedUnion('type', [
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('hello'),
        deviceId: uuid,
        keyId: z.string().regex(/^sha256:[a-f0-9]{64}$/),
        publicKey: z.string().min(40).max(500),
        offlinePackageKey: readerOfflinePackageKeySchema.optional(),
        installedOfflinePackage: installedOfflinePackageSchema
            .nullable()
            .optional(),
        agentVersion: z.string().min(1).max(40),
        capabilities: z.array(z.literal('nfc_mifare_classic_1k_uid')),
        status: cardReaderStatusSchema,
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('status'),
        status: cardReaderStatusSchema,
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('sequence-floor-set'),
        sequence: z.number().int().nonnegative(),
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('read'),
        attestation: signedCardReadAttestationSchema,
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('cancelled'),
        readRequestId: uuid,
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('offline_package_installed'),
        package: installedOfflinePackageSchema,
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('offline-read'),
        attestation: signedOfflineTerminalMatchSchema,
    }),
    z.object({
        version: cardReaderProtocolVersionSchema,
        requestId: uuid,
        type: z.literal('error'),
        code: z.enum([
            'invalid_request',
            'reader_unavailable',
            'pcsc_unavailable',
            'card_timeout',
            'read_cancelled',
            'card_unsupported',
            'offline_package_invalid',
            'offline_package_storage_failed',
            'offline_package_not_ready',
            'offline_card_not_eligible',
            'internal_error',
        ]),
        message: z.string().min(1).max(500),
    }),
]);
export function canonicalCardReadPayload(payload) {
    return JSON.stringify({
        challenge: payload.challenge,
        deviceId: payload.deviceId,
        institutionId: payload.institutionId,
        purpose: payload.purpose,
        readAt: payload.readAt,
        readerName: payload.readerName,
        requestId: payload.requestId,
        sequence: payload.sequence,
        terminalId: payload.terminalId,
        uid: payload.uid,
        version: payload.version,
    });
}
export function canonicalOfflineTerminalMatchPayload(payload) {
    return canonicalJson({
        assignmentVersion: payload.assignmentVersion,
        authorization: payload.authorization,
        consumerId: payload.consumerId,
        deviceId: payload.deviceId,
        institutionId: payload.institutionId,
        packageId: payload.packageId,
        readAt: payload.readAt,
        readerName: payload.readerName,
        receiptId: payload.receiptId,
        requestId: payload.requestId,
        sequence: payload.sequence,
        terminalId: payload.terminalId,
        version: payload.version,
    });
}
function canonicalJson(value) {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value);
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    const record = value;
    return `{${Object.keys(record)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
        .join(',')}}`;
}
